import { Building2, Truck, Users, Factory, Store, Wrench, Shield, ArrowRight, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

export const CommercialInsurancePage = () => {
  const insuranceProducts = [
    {
      icon: Building2,
      title: "General Liability Insurance",
      description: "Protect your business from third-party claims of bodily injury or property damage",
      features: [
        "Bodily injury coverage",
        "Property damage protection",
        "Medical payments coverage",
        "Legal defense costs",
      ],
    },
    {
      icon: Store,
      title: "Commercial Property Insurance",
      description: "Coverage for your business property, equipment, and inventory",
      features: [
        "Building and structure coverage",
        "Business personal property",
        "Equipment breakdown protection",
        "Loss of income coverage",
      ],
    },
    {
      icon: Truck,
      title: "Commercial Auto Insurance",
      description: "Comprehensive coverage for vehicles used in your business operations",
      features: [
        "Liability coverage",
        "Physical damage protection",
        "Hired and non-owned auto",
        "Uninsured motorist coverage",
      ],
    },
    {
      icon: Users,
      title: "Workers' Compensation",
      description: "Required coverage to protect your employees in case of work-related injuries",
      features: [
        "Medical expense coverage",
        "Lost wages compensation",
        "Disability benefits",
        "Return-to-work programs",
      ],
    },
    {
      icon: Wrench,
      title: "Professional Liability",
      description: "Errors and omissions coverage for professional service providers",
      features: [
        "Professional negligence protection",
        "Legal defense coverage",
        "Client lawsuits protection",
        "Regulatory defense costs",
      ],
    },
    {
      icon: Factory,
      title: "Business Owners Policy (BOP)",
      description: "Bundled coverage combining general liability and property insurance",
      features: [
        "Package policy convenience",
        "Cost-effective premiums",
        "Comprehensive protection",
        "Business interruption coverage",
      ],
    },
  ];

  const industries = [
    "Construction & Contractors",
    "Retail & Restaurants",
    "Professional Services",
    "Manufacturing",
    "Healthcare Providers",
    "Technology Companies",
    "Real Estate",
    "Hospitality",
    "Transportation & Logistics",
    "Agriculture",
    "Nonprofit Organizations",
    "Wholesale Distributors",
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <Building2 className="h-20 w-20 mx-auto mb-6 text-blue-100" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Commercial Insurance Solutions</h1>
            <p className="text-xl text-blue-100 mb-8">
              Comprehensive business protection tailored to your industry and unique risks
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100 font-semibold px-8">
                Get Your Business Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Banner with Business Image */}
      <section className="relative section-padding overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/6950093/pexels-photo-6950093.jpeg?w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-blue-700/90"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Your Business Success is Our Priority
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Comprehensive commercial insurance solutions backed by decades of expertise and industry-leading partnerships
              </p>
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100 font-semibold px-8">
                  Get Your Business Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6 text-white">
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">1000+</div>
                    <div className="text-sm text-blue-100">Businesses Protected</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">15+</div>
                    <div className="text-sm text-blue-100">Industries Served</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">98%</div>
                    <div className="text-sm text-blue-100">Satisfaction Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">24/7</div>
                    <div className="text-sm text-blue-100">Claims Support</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Insurance Products */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Commercial Insurance Products
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Protect your business assets, employees, and operations with comprehensive coverage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {insuranceProducts.map((product, index) => (
              <Card key={index} className="card-hover border-2 hover:border-primary">
                <CardHeader>
                  <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                    <product.icon className="h-7 w-7 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{product.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4 text-sm">{product.description}</p>
                  <ul className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Specialized insurance solutions for diverse business sectors
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <Card key={index} className="card-hover hover:border-primary border-2 text-center">
                <CardContent className="pt-6 pb-6">
                  <Shield className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground">{industry}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
              Why Businesses Choose Henry T. Warner Co.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1 text-foreground">Industry Expertise</h3>
                    <p className="text-muted-foreground">
                      Deep understanding of the unique risks and coverage needs across multiple industries
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1 text-foreground">Multiple Carrier Access</h3>
                    <p className="text-muted-foreground">
                      Compare rates and coverage from leading commercial insurance providers
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1 text-foreground">Risk Assessment</h3>
                    <p className="text-muted-foreground">
                      Comprehensive evaluation of your business risks to ensure adequate protection
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1 text-foreground">Claims Advocacy</h3>
                    <p className="text-muted-foreground">
                      Expert support throughout the claims process to protect your interests
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1 text-foreground">Cost Optimization</h3>
                    <p className="text-muted-foreground">
                      Strategic bundling and risk management to reduce your insurance costs
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1 text-foreground">Ongoing Support</h3>
                    <p className="text-muted-foreground">
                      Regular policy reviews and updates as your business grows and evolves
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-bold mb-4 text-foreground">Protect Your Business Today</h3>
              <p className="text-muted-foreground mb-6">
                Get a customized commercial insurance quote tailored to your business needs
              </p>
              <Link to="/contact">
                <Button size="lg" className="font-semibold px-8">
                  Request Commercial Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <div className="mt-6 text-sm text-muted-foreground">
                <p>Licensed in MN, WI, IA, ND, SD, TN, and AZ</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
